/**
 * Code splitting: the match scene (sim, CPU, netcode, modes, instant replays) is its own chunk. The menus load
 * without it; it is fetched when the browser is idle after the first paint, or at the latest by the Versus splash
 * (`prepare`) / the match itself (`enter`). This proxy is the Scene the SceneManager knows; everything forwards to
 * the real MatchScene once its chunk is in.
 */
import type { ModeCommand } from '@hf/client-core/modes/types';
import type { MatchScene } from './match-scene';
import type { Scene } from '@hf/client-core/match/scene-manager';
import type { MatchSetup } from '@hf/client-core/ui-model/types';

export class LazyMatchScene implements Scene {
  readonly id = 'match' as const;
  private inner: MatchScene | null = null;
  private loading: Promise<MatchScene> | null = null;
  /** The setup a `prepare()` asked for while the chunk was still loading (null = dropped since). */
  private wanted: MatchSetup | null = null;

  constructor(private readonly load: () => Promise<MatchScene>) {}

  private get(): Promise<MatchScene> {
    this.loading ??= this.load().then(
      (s) => (this.inner = s),
      (e: unknown) => {
        this.loading = null; // retry on the next use (a flaky network, not a broken build)
        throw e;
      },
    );
    return this.loading;
  }

  /** Fetch the chunk now (idle time), without doing anything else. */
  preload(): void {
    this.get().catch(() => undefined);
  }

  /** The loaded scene (null until its chunk arrived). */
  get scene(): MatchScene | null {
    return this.inner;
  }

  async enter(): Promise<void> {
    this.wanted = null;
    const s = await this.get();
    await s.enter();
  }

  exit(): void {
    this.inner?.exit();
  }

  prepare(setup: MatchSetup): void {
    if (this.inner) {
      this.inner.prepare(setup);
      return;
    }
    this.wanted = setup;
    this.get().then(
      (s) => {
        if (this.wanted === setup) s.prepare(setup);
        this.wanted = null;
      },
      () => undefined,
    );
  }

  dropPrepared(): void {
    this.wanted = null;
    this.inner?.dropPrepared();
  }

  command(cmd: ModeCommand): void {
    this.inner?.command(cmd);
  }

  get tick(): number {
    return this.inner?.tick ?? -1;
  }

  get currentDriver(): MatchScene['currentDriver'] {
    return this.inner?.currentDriver ?? null;
  }

  get confirmed(): MatchScene['confirmed'] {
    return this.inner?.confirmed ?? null;
  }

  get instantReplay(): MatchScene['instantReplay'] {
    return this.inner?.instantReplay ?? null;
  }
}
